import React, { useState, useEffect, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import moment from "moment";
import config, { MARKETING } from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import Login from "../../Authentication/Login";
import MarketingTabs from "../AllTabs/MarketingTabs";

const LeadCalendar = () => {
  const { user, loading } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to get color based on lead status
  const getEventColor = (status) => {
    switch(status?.toUpperCase()) {
      case 'NEW': return '#28a745'; // Green
      case 'CONTACTED': return '#17a2b8'; // Blue
      case 'QUALIFIED': return '#ffc107'; // Yellow
      case 'LOST': return '#dc3545'; // Red
      case 'WON': return '#28a745'; // Green
      case 'CANCELLED': return '#dc3545'; // Red
      default: return '#6c757d'; // Gray
    }
  };

  // Helper function to render service details based on enquiry type
  const renderServiceDetails = (serviceDetails, enquiryType) => {
    if (!serviceDetails) return null;

    switch(enquiryType?.toUpperCase()) {
      case 'HOTEL':
      case 'HOTELS':
        return (
          <div className="row">
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                
                  <tr>
                    <td className="text-muted">City:</td>
                    <td className="fw-bold">{serviceDetails.city || serviceDetails.propertyCity || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Country:</td>
                    <td className="fw-bold">{serviceDetails.country || serviceDetails.propertyCountry || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Check In:</td>
                    <td className="fw-bold">{serviceDetails.checkIn ? moment(serviceDetails.checkIn).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Check Out:</td>
                    <td className="fw-bold">{serviceDetails.checkOut ? moment(serviceDetails.checkOut).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Adults:</td>
                    <td className="fw-bold">{serviceDetails.adults || 0}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Children:</td>
                    <td className="fw-bold">{serviceDetails.children || 0}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Rooms:</td>
                    <td className="fw-bold">{serviceDetails.rooms || 1}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Total Guests:</td>
                    <td className="fw-bold">{serviceDetails.totalGuests || 
                      (parseInt(serviceDetails.adults || 0) + parseInt(serviceDetails.children || 0))}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Room Preference:</td>
                    <td className="fw-bold">{serviceDetails.roomPreference || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serviceDetails.specialRequests && (
              <div className="col-12 mt-2">
                <div className="bg-light p-2 rounded">
                  <span className="text-muted">Special Requests:</span>
                  <p className="mb-0 mt-1">{serviceDetails.specialRequests}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'FLIGHTS':
        return (
          <div className="row">
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>From:</td>
                    <td className="fw-bold">{serviceDetails.from || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">To:</td>
                    <td className="fw-bold">{serviceDetails.to || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Departure Date:</td>
                    <td className="fw-bold">{serviceDetails.departureDate ? moment(serviceDetails.departureDate).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Return Date:</td>
                    <td className="fw-bold">{serviceDetails.returnDate ? moment(serviceDetails.returnDate).format("DD-MMM-YYYY") : 'One Way'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Passengers:</td>
                    <td className="fw-bold">{serviceDetails.passengers || 1}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Class:</td>
                    <td className="fw-bold">{serviceDetails.classType || 'Economy'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serviceDetails.requirements && (
              <div className="col-12 mt-2">
                <div className="bg-light p-2 rounded">
                  <span className="text-muted">Requirements:</span>
                  <p className="mb-0 mt-1">{serviceDetails.requirements}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'VISA':
        return (
          <div className="row">
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Visa Type:</td>
                    <td className="fw-bold">{serviceDetails.visaType || 'Tourist'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Destination Country:</td>
                    <td className="fw-bold">{serviceDetails.destinationCountry || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Applicants:</td>
                    <td className="fw-bold">{serviceDetails.applicants || 1}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Processing Time:</td>
                    <td className="fw-bold">{serviceDetails.processingTime || 'Standard'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serviceDetails.requirements && (
              <div className="col-12 mt-2">
                <div className="bg-light p-2 rounded">
                  <span className="text-muted">Requirements:</span>
                  <p className="mb-0 mt-1">{serviceDetails.requirements}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'SIGHTSEEING':
        return (
          <div className="row">
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Location:</td>
                    <td className="fw-bold">{serviceDetails.location || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Activity Type:</td>
                    <td className="fw-bold">{serviceDetails.activityType || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Date:</td>
                    <td className="fw-bold">{serviceDetails.date ? moment(serviceDetails.date).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Participants:</td>
                    <td className="fw-bold">{serviceDetails.participants || 1}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serviceDetails.requirements && (
              <div className="col-12 mt-2">
                <div className="bg-light p-2 rounded">
                  <span className="text-muted">Requirements:</span>
                  <p className="mb-0 mt-1">{serviceDetails.requirements}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'PACKAGE':
        return (
          <div className="row">
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Destination:</td>
                    <td className="fw-bold">{serviceDetails.destination || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Check In:</td>
                    <td className="fw-bold">{serviceDetails.checkIn ? moment(serviceDetails.checkIn).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Check Out:</td>
                    <td className="fw-bold">{serviceDetails.checkOut ? moment(serviceDetails.checkOut).format("DD-MMM-YYYY") : 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Adults:</td>
                    <td className="fw-bold">{serviceDetails.adults || 1}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Children:</td>
                    <td className="fw-bold">{serviceDetails.children || 0}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Nights:</td>
                    <td className="fw-bold">{serviceDetails.nights || 
                      moment(serviceDetails.checkOut).diff(moment(serviceDetails.checkIn), 'days') || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {serviceDetails.requirements && (
              <div className="col-12 mt-2">
                <div className="bg-light p-2 rounded">
                  <span className="text-muted">Requirements:</span>
                  <p className="mb-0 mt-1">{serviceDetails.requirements}</p>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="row">
            <div className="col-12">
              <table className="table table-sm table-borderless">
                <tbody>
                  {Object.entries(serviceDetails).map(([key, value]) => (
                    <tr key={key}>
                      <td className="text-muted" style={{ width: '40%' }}>{key}:</td>
                      <td className="fw-bold">{value?.toString() || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
    }
  };

  const getLeadData = useCallback(
    async (viewInfo) => {
      if (!user || !viewInfo) return;
      
      setIsLoading(true);
      
      try {
        const resourceUrl = `${config.GETLEADSBYUSER}/${user.id}?pageNumber=1&pageSize=500`;
        
        console.log("Fetching leads from:", resourceUrl);
        
        const response = await apiService.getAll(resourceUrl);
        
        console.log("API Response:", response);
        
        // Handle different response structures
        let leadsData = [];
        
        if (response) {
          if (response.data && Array.isArray(response.data)) {
            leadsData = response.data;
          } 
          else if (Array.isArray(response)) {
            leadsData = response;
          }
          else if (response.items && Array.isArray(response.items)) {
            leadsData = response.items;
          }
        }
        
        // Filter leads that have callback dates
        const leadsWithCallback = leadsData.filter(
          (lead) => lead.callBack && lead.callBack !== null && lead.callBack !== ""
        );
        
        console.log("Leads with callback:", leadsWithCallback);
        
        if (leadsWithCallback.length === 0) {
          setEvents([]);
          setIsLoading(false);
          return;
        }
        
        // Transform leads to calendar events
        const calendarEvents = leadsWithCallback.map((lead) => {
          const callbackDate = moment(lead.callBack);
          
          if (!callbackDate.isValid()) {
            return null;
          }
          
          let title = lead.name || 'No name';
          if (lead.enquiryType) {
            title = `${title} (${lead.enquiryType})`;
          }
          
          return {
            id: lead.leadId,
            title: title,
            start: callbackDate.toISOString(),
            end: callbackDate.toISOString(),
            allDay: false,
            extendedProps: { 
              ...lead,
              formattedCallback: callbackDate.format("DD-MMM-YYYY, hh:mm A")
            },
            className: "calendar-event",
            backgroundColor: getEventColor(lead.leadStatus),
            borderColor: getEventColor(lead.leadStatus),
            textColor: "#ffffff"
          };
        }).filter(event => event !== null);
        
        console.log("Calendar events created:", calendarEvents);
        setEvents(calendarEvents);
        
      } catch (error) {
        console.error("Error fetching lead data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  const handleDatesSet = (viewInfo) => {
    getLeadData(viewInfo);
  };

  const handleEventClick = (clickInfo) => {
    console.log("Event clicked:", clickInfo.event);
    setSelectedEvent(clickInfo.event.extendedProps);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
  
  if (!user) return <Login />;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <MarketingTabs pageName="APPOINTMENT" />
              </div>
              <div className="card-body">
                <div className="col-12 pb-2 fs-5 d-flex justify-content-between align-items-center">
                  <span>Appointments</span>
                  {events.length > 0 && (
                    <span className="badge bg-primary">
                      {events.length} Appointment{events.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                
                <style>
                  {`
                    .calendar-event {
                      color: white !important;
                      cursor: pointer !important;
                      border-radius: 4px !important;
                      padding: 2px 4px !important;
                      font-weight: 500 !important;
                    }
                    .calendar-event:hover {
                      opacity: 0.9 !important;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
                    }
                    .fc-event {
                      cursor: pointer !important;
                    }
                    .fc-event-title {
                      color: white !important;
                      font-weight: 500 !important;
                    }
                    .fc .fc-event {
                      color: white !important;
                      border: none !important;
                    }
                    .fc-day-today {
                      background-color: rgba(52, 152, 219, 0.05) !important;
                    }
                  `}
                </style>
                
                <FullCalendar
                  plugins={[
                    dayGridPlugin,
                    timeGridPlugin,
                    listPlugin,
                    bootstrap5Plugin,
                  ]}
                  themeSystem="bootstrap5"
                  headerToolbar={{
                    left: "today prev,next",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                  }}
                  buttonText={{
                    today: "Today",
                    month: "Month",
                    week: "Week",
                    day: "Day",
                    list: "Agenda",
                  }}
                  initialView="dayGridMonth"
                  height="75vh"
                  events={events}
                  datesSet={handleDatesSet}
                  eventClick={handleEventClick}
                  eventDisplay="block"
                  displayEventTime={true}
                  displayEventEnd={true}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: 'short',
                    hour12: true
                  }}
                  slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: 'short',
                    hour12: true
                  }}
                  dayMaxEvents={true}
                  weekends={true}
                  nowIndicator={true}
                  eventBackgroundColor="transparent"
                  eventBorderColor="transparent"
                  eventTextColor="#ffffff"
                />
                
                {/* Event Details Modal */}
                <Modal 
                  show={showModal} 
                  onHide={handleCloseModal} 
                  centered 
                  size="lg"
                >
                  <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                      <i className="fas fa-calendar-check me-2"></i>
                      Appointment Details
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {selectedEvent && (
                      <div className="container-fluid">
                        <div className="row">
                          {/* Customer Information */}
                          <div className="col-md-6 mb-3">
                            <div className="card h-100 border-0 shadow-sm">
                              <div className="card-header bg-transparent border-0">
                                <h6 className="mb-0">
                                  <i className="fas fa-user me-2 text-primary"></i>
                                  Customer Information
                                </h6>
                              </div>
                              <div className="card-body pt-0">
                                <table className="table table-sm table-borderless">
                                  <tbody>
                                    <tr>
                                      <td className="text-muted" style={{ width: '40%' }}>Name:</td>
                                      <td className="fw-bold">{selectedEvent.name || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Phone:</td>
                                      <td className="fw-bold">
                                        <a href={`tel:${selectedEvent.phoneNumber}`} className="text-decoration-none">
                                          {selectedEvent.phoneNumber || 'N/A'}
                                        </a>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Email:</td>
                                      <td className="fw-bold">
                                        {selectedEvent.email ? (
                                          <a href={`mailto:${selectedEvent.email}`} className="text-decoration-none">
                                            {selectedEvent.email}
                                          </a>
                                        ) : 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Source:</td>
                                      <td>
                                        <span className="badge bg-secondary">
                                          {selectedEvent.sourceName || 'N/A'}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          
                          {/* Appointment Information - With Raw Callback Value */}
                          <div className="col-md-6 mb-3">
                            <div className="card h-100 border-0 shadow-sm">
                              <div className="card-header bg-transparent border-0">
                                <h6 className="mb-0">
                                  <i className="fas fa-clock me-2 text-success"></i>
                                  Appointment Information
                                </h6>
                              </div>
                              <div className="card-body pt-0">
                                <table className="table table-sm table-borderless">
                                  <tbody>
                                    <tr>
                                      <td className="text-muted" style={{ width: '40%' }}>Follow UP Time:</td>
                                      <td className="fw-bold text-primary">
                                        {selectedEvent.callBack || 'N/A'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Enquiry Type:</td>
                                      <td>
                                        <span className="badge bg-info">
                                          {selectedEvent.enquiryType || 'N/A'}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Lead Status:</td>
                                      <td>
                                        <span className="badge" style={{ 
                                          backgroundColor: getEventColor(selectedEvent.leadStatus),
                                          color: '#ffffff'
                                        }}>
                                          {selectedEvent.leadStatus || 'NEW'}
                                        </span>
                                      </td>
                                    </tr>
                                   
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          
                          {/* Enquiry Details Section */}
                          <div className="col-12 mb-3">
                            <div className="card border-0 shadow-sm">
                              <div className="card-header bg-transparent border-0">
                                <h6 className="mb-0">
                                  <i className="fas fa-info-circle me-2 text-info"></i>
                                  Enquiry Details - {selectedEvent.enquiryType || 'General'}
                                </h6>
                              </div>
                              <div className="card-body pt-0">
                                {/* Render service details based on enquiry type */}
                                {selectedEvent.serviceDetails ? (
                                  renderServiceDetails(selectedEvent.serviceDetails, selectedEvent.enquiryType)
                                ) : (
                                  <p className="text-muted mb-0">No service details available</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                         
                          
                        </div>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="bg-light">
                    <div className="d-flex justify-content-between w-100">
                      <div>
                        {/* {selectedEvent?.phoneNumber && (
                          <a 
                            href={`tel:${selectedEvent.phoneNumber}`} 
                            className="btn btn-outline-primary me-2"
                          >
                            <i className="fas fa-phone me-1"></i> Call
                          </a>
                        )}
                        {selectedEvent?.email && (
                          <a 
                            href={`mailto:${selectedEvent.email}`} 
                            className="btn btn-outline-secondary me-2"
                          >
                            <i className="fas fa-envelope me-1"></i> Email
                          </a>
                        )} */}
                      </div>
                      <div>
                        <button
                          className="btn btn-danger"
                          onClick={handleCloseModal}
                        >
                          <i className="fas fa-times me-1"></i> Close
                        </button>
                      </div>
                    </div>
                  </Modal.Footer>
                </Modal>
                
                {/* No events message */}
                {!isLoading && events.length === 0 && (
                  <div className="text-center p-5">
                    <div className="mb-3">
                      <i className="fas fa-calendar-times fa-3x text-muted"></i>
                    </div>
                    <h5 className="text-muted">No Appointments Found</h5>
                    <p className="text-muted mb-0">
                      There are no callback appointments scheduled for the selected date range.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadCalendar;